/**
 * Alpine x-track-form directive — form abandonment tracking.
 *
 * Attaches a form tracker to the nearest <form> ancestor (or the element
 * itself if it is a form). Automatically cleans up on Alpine teardown.
 *
 * Usage:
 *   <form x-track-form="'registration'">            — string → formName
 *   <form x-track-form="{ formName: 'signup' }">    — object → full config
 *   <div x-track-form="'login'">                    — finds nearest <form> ancestor
 */

import { createFormTracker } from '../utils/form-tracker';

/**
 * Register the x-track-form directive on the given Alpine instance.
 *
 * @param {object} Alpine
 */
export default function registerTrackFormDirective(Alpine) {
  Alpine.directive(
    'track-form',
    (el, { expression }, { evaluate, cleanup }) => {
      const value = expression ? evaluate(expression) : null;
      if (!value) return;

      // Resolve config: string → { formName }, object → passthrough
      const config = typeof value === 'string' ? { formName: value } : value;

      if (!config.formName) return;

      // Find the form element: self if <form>, otherwise closest ancestor
      const form = el.tagName === 'FORM' ? el : el.closest('form');
      if (!form) return;

      const tracker = createFormTracker(form, config);

      cleanup(() => {
        tracker.destroy();
      });
    }
  );
}
